import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";

const DEFAULT_ROW_COUNT = 5;

const isValidQuantification = (value) => {
  if (value.trim() === "") return true;
  return /^\d+(\.\d*)?$/.test(value.trim());
};

const toDisplayText = (rows) =>
  rows
    .filter(
      (row) => row.substance.trim() !== "" || row.quantification.trim() !== "",
    )
    .map((row) => {
      const substance = row.substance.trim();
      const quantification = row.quantification.trim();

      if (substance && quantification)
        return `${substance} (${quantification}%)`;
      if (substance) return substance;
      return `${quantification}%`;
    })
    .join(", ");

const parseLegacyResultsText = (resultsText) => {
  if (!resultsText || typeof resultsText !== "string") return [];

  const segments = resultsText
    .split(/\n|,|;/)
    .map((item) => item.trim())
    .filter(Boolean);

  return segments.map((segment) => {
    const fullMatch = segment.match(
      /^(.*?)(?:\s*\(?\s*)(\d+(?:\.\d+)?)\s*%?\)?$/,
    );
    if (fullMatch) {
      return {
        substance: fullMatch[1].trim(),
        quantification: fullMatch[2].trim(),
      };
    }

    return {
      substance: segment,
      quantification: "",
    };
  });
};

const getInitialRows = (formData) => {
  if (Array.isArray(formData?.ftirResults) && formData.ftirResults.length > 0) {
    const normalizedRows = formData.ftirResults.map((row) => ({
      substance: row?.substance ? String(row.substance) : "",
      quantification: row?.quantification ? String(row.quantification) : "",
    }));

    if (normalizedRows.length >= DEFAULT_ROW_COUNT) return normalizedRows;
    return [
      ...normalizedRows,
      ...Array.from(
        { length: DEFAULT_ROW_COUNT - normalizedRows.length },
        () => ({
          substance: "",
          quantification: "",
        }),
      ),
    ];
  }

  const parsedRows = parseLegacyResultsText(formData?.results);
  if (parsedRows.length >= DEFAULT_ROW_COUNT) return parsedRows;

  return [
    ...parsedRows,
    ...Array.from({ length: DEFAULT_ROW_COUNT - parsedRows.length }, () => ({
      substance: "",
      quantification: "",
    })),
  ];
};

const FTIRForm = ({ formData, handleChange, showValidationError }) => {
  const [rows, setRows] = useState(() => getInitialRows(formData));

  useEffect(() => {
    setRows(getInitialRows(formData));
  }, [formData?.resultId, formData?.testType]);

  const syncRows = (updatedRows) => {
    setRows(updatedRows);

    const populatedRows = updatedRows
      .filter(
        (row) =>
          row.substance.trim() !== "" || row.quantification.trim() !== "",
      )
      .map((row) => ({
        substance: row.substance.trim(),
        quantification: row.quantification.trim(),
      }));

    handleChange("ftirResults", populatedRows);
    handleChange("results", toDisplayText(updatedRows));
  };

  const handleRowChange = (index, field, value) => {
    const updatedRows = rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: value } : row,
    );

    syncRows(updatedRows);
  };

  const addRow = () => {
    syncRows([
      ...rows,
      {
        substance: "",
        quantification: "",
      },
    ]);
  };

  const hasResultRows = rows.some(
    (row) => row.substance.trim() !== "" || row.quantification.trim() !== "",
  );

  const hasInvalidQuantification = rows.some(
    (row) =>
      row.quantification.trim() !== "" &&
      !isValidQuantification(row.quantification),
  );

  const hasMissingSubstance = rows.some(
    (row) => row.quantification.trim() !== "" && row.substance.trim() === "",
  );

  const getRowError = (row) => {
    if (
      row.quantification.trim() !== "" &&
      !isValidQuantification(row.quantification)
    ) {
      return "Quantification must be a number";
    }
    if (row.quantification.trim() !== "" && row.substance.trim() === "") {
      return "Substance is required when quantification is provided";
    }
    return null;
  };

  return (
    <>
      <Form.Group>
        <Form.Label className='fs-6 fw-semibold'>Results</Form.Label>
        {rows.map((row, index) => {
          const rowError = getRowError(row);
          const isMissingSubstance =
            row.quantification.trim() !== "" && row.substance.trim() === "";

          return (
            <div key={`ftir-row-${index}`} className='mb-3'>
              <div className='d-flex gap-2 align-items-end'>
                <Form.Group className='flex-grow-1 mb-0'>
                  <Form.Label className='small mb-1'>Substance</Form.Label>
                  <Form.Control
                    type='text'
                    value={row.substance}
                    onChange={(e) => {
                      handleRowChange(index, "substance", e.target.value);
                    }}
                    placeholder='Enter substance'
                    isInvalid={isMissingSubstance}
                  />
                </Form.Group>
                <Form.Group style={{ width: "180px" }} className='mb-0'>
                  <Form.Label className='small mb-1'>Quantification</Form.Label>
                  <div className='d-flex gap-1 align-items-center'>
                    <Form.Control
                      type='text'
                      value={row.quantification}
                      onChange={(e) => {
                        handleRowChange(
                          index,
                          "quantification",
                          e.target.value,
                        );
                      }}
                      placeholder='0'
                      aria-label='Quantification'
                      isInvalid={!!rowError}
                      title='Must be a number (decimals allowed)'
                    />
                    <span>%</span>
                  </div>
                </Form.Group>
              </div>
              {rowError && (
                <div className='text-danger small mt-1'>{rowError}</div>
              )}
            </div>
          );
        })}
        <Button
          variant='outline-primary'
          type='button'
          className='mt-1 mb-3'
          onClick={addRow}
          aria-label='Add FTIR row'
          title='Add FTIR row'
        >
          +
        </Button>
      </Form.Group>
      <Form.Group>
        <Form.Label className='fs-6 fw-semibold'>Comments:</Form.Label>
        <Form.Control
          as='textarea'
          rows={3}
          defaultValue={formData.comments}
          onChange={(e) => {
            handleChange("comments", e.target.value);
          }}
        />
      </Form.Group>
      {showValidationError &&
        (!hasResultRows || hasInvalidQuantification || hasMissingSubstance) && (
          <div className='invalid-feedback d-block'>
            {!hasResultRows && "Please enter at least one FTIR result. "}
            {hasInvalidQuantification &&
              "Quantification must be a valid number (decimals allowed). "}
            {hasMissingSubstance &&
              "Substance is required when quantification is provided."}
          </div>
        )}
    </>
  );
};

export default FTIRForm;
